/**
 * Axios API
 * Middleware of api request and response
 */

import axios, { AxiosError } from 'axios';
import { API_BASE_URL, API_TIMEOUT } from '../config/api';

/**
 * Creating custom instance for config
 * server configurations.
 * baseUrl, timeout, api-token etc.
 */

const httpRequest = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * axios request interceptors for debugging
 * and modify request data
 */

httpRequest.interceptors.request.use(
  async (reqConfig) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      reqConfig.headers.Authorization = `Bearer ${token}`;
    }

    return reqConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Customizing axios success and error
 * data to easily handle them in app
 */
httpRequest.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('response', response.data);
    }
    return response?.data;
  },
  async (error) => {
    console.error('error', error?.response || error?.message || error);

    // Backend returns 401 for token errors (authMiddleware.js):
    // - "Not authorized, token failed" (invalid/expired JWT)
    // - "Not authorized, user not found"
    // - "Not authorized, no token"
    if (error?.response?.status === 401) {
      localStorage.removeItem('authToken');
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      if (path !== '/login') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(handleApiError(error));
  }
);

/**
 * Handling error
 * @param {*} error
 * @returns
 */
const handleApiError = (error: AxiosError | any) => {
  if (process.env.NODE_ENV === 'development') {
    console.error('error', error?.response?.data || error?.message || error);
  }
  if (error?.response) {
    return error?.response?.data
      ? {
          message:
            error?.response?.data?.error?.message ||
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Something went wrong. Please try again.',
        }
      : 'Something went wrong. Please try again.';
  } else if (error?.request) {
    return new Error('No response from server. Check your internet or server.');
  } else {
    return new Error(error.message || 'Unknown error occurred.');
  }
};

export { httpRequest };

